export type EntryIntent = "rest" | "direction" | "connection" | "surprise";

export type SeaState = "calm" | "swell" | "storm";

export type Recommendation = "special" | "heart-to-heart" | "the-wave";

export type HotspotId = "sea" | "journal" | "hearth" | "menu" | "boards";

export type PanelId =
  | HotspotId
  | "room-list"
  | "contact"
  | "newsletter"
  | "takeaway"
  | "privacy"
  | "about";

export type ExperiencePhase = "threshold" | "opening" | "inside";

export type ExperienceState = {
  phase: ExperiencePhase;
  intent?: EntryIntent;
  seaState?: SeaState;
  activePanel: PanelId | null;
  discoveredHotspots: HotspotId[];
  soundEnabled: boolean;
  recommendation?: Recommendation;
};

export type ExperienceAction =
  | { type: "select-intent"; intent?: EntryIntent }
  | { type: "enter" }
  | { type: "finish-opening" }
  | { type: "open-hotspot"; hotspot: HotspotId }
  | { type: "open-panel"; panel: PanelId }
  | { type: "close-panel" }
  | { type: "set-sea-state"; seaState: SeaState }
  | { type: "complete-soul-check"; recommendation: Recommendation }
  | { type: "set-sound"; enabled: boolean }
  | { type: "leave" };

export const defaultExperienceState: ExperienceState = {
  phase: "threshold",
  activePanel: null,
  discoveredHotspots: [],
  soundEnabled: false,
};

function addUniqueHotspot(
  hotspots: HotspotId[],
  hotspot: HotspotId,
): HotspotId[] {
  return hotspots.includes(hotspot) ? hotspots : [...hotspots, hotspot];
}

export function experienceReducer(
  state: ExperienceState,
  action: ExperienceAction,
): ExperienceState {
  switch (action.type) {
    case "select-intent":
      return { ...state, intent: action.intent };
    case "enter":
      return state.phase === "threshold" ? { ...state, phase: "opening" } : state;
    case "finish-opening":
      return state.phase === "opening" ? { ...state, phase: "inside" } : state;
    case "open-hotspot":
      return {
        ...state,
        phase: "inside",
        activePanel: action.hotspot,
        discoveredHotspots: addUniqueHotspot(
          state.discoveredHotspots,
          action.hotspot,
        ),
      };
    case "open-panel":
      return { ...state, activePanel: action.panel };
    case "close-panel":
      return { ...state, activePanel: null };
    case "set-sea-state":
      return { ...state, seaState: action.seaState };
    case "complete-soul-check":
      return {
        ...state,
        recommendation: action.recommendation,
      };
    case "set-sound":
      return { ...state, soundEnabled: action.enabled };
    case "leave":
      return {
        ...state,
        phase: "threshold",
        intent: undefined,
        seaState: undefined,
        activePanel: null,
        recommendation: undefined,
        soundEnabled: false,
      };
  }
}

const PREFERENCES_KEY = "soul-surf-shack:preferences:v1";

type StoredPreferences = Pick<
  ExperienceState,
  "discoveredHotspots" | "soundEnabled"
>;

function isHotspotId(value: unknown): value is HotspotId {
  return ["sea", "journal", "hearth", "menu", "boards"].includes(
    String(value),
  );
}

export function loadPreferences(storage: Storage | undefined): StoredPreferences {
  if (!storage) {
    return { discoveredHotspots: [], soundEnabled: false };
  }

  try {
    const stored = JSON.parse(storage.getItem(PREFERENCES_KEY) ?? "{}") as {
      discoveredHotspots?: unknown;
      soundEnabled?: unknown;
    };

    const discoveredHotspots = Array.isArray(stored.discoveredHotspots)
      ? stored.discoveredHotspots.filter(isHotspotId)
      : [];

    return {
      discoveredHotspots,
      soundEnabled: stored.soundEnabled === true,
    };
  } catch {
    return { discoveredHotspots: [], soundEnabled: false };
  }
}

export function savePreferences(
  storage: Storage | undefined,
  state: ExperienceState,
): void {
  if (!storage) {
    return;
  }

  const preferences: StoredPreferences = {
    discoveredHotspots: state.discoveredHotspots,
    soundEnabled: state.soundEnabled,
  };

  try {
    storage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  } catch {
    // The experience continues without persistence when storage is unavailable.
  }
}

export function createInitialExperienceState(
  storage: Storage | undefined,
): ExperienceState {
  return {
    ...defaultExperienceState,
    ...loadPreferences(storage),
  };
}
